import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {
  AppDatePicker,
  Col,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiImage,
  UiInput,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UploadResponse,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import UploadImageModal from '~/components/modals/UploadImageModal';
import ViewImageModal from '~/components/modals/ViewImageModal';
import {
  BIRTH_CONTROL_REFILL_KEYS,
  PHOTO_ID_TYPE_OPTIONS,
  PHOTO_ID_TYPE_VALUES,
  SEX_OPTIONS,
  SEX_VALUES,
} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {UserProfile} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {
  CALENDAR_DATE_TIME_FORMAT,
  checkYMDIsValid,
  convertDMYToYMD,
  DATE_OF_BIRTH_TIME_FORMAT,
  formatDate,
  isUnderAge,
} from '~/utils/helpers';
import {spacing} from '~/utils/theme/spacing';
import PurchaseNotFoundModal from '~/pages/homes/birth-control-refill/PurchaseNotFoundModal';
import moment from 'moment';
import {DrugAllergies} from '~/components/shared/DrugAllergies';

const Questionnaire2Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REFILL_KEYS.PROFILE);
  const lastBCQuestionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REFILL_KEYS.LAST_BC_PURCHASE);

  const product = lastBCQuestionnaire?.questions?.product;

  const currentUser = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REFILL_KEYS.PROFILE]?.data?.user,
  );
  const [isShowPhotoId, setIsShowPhotoId] = useState(false);
  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  const [isShowPurchaseNotFoundModal, setIsShowPurchaseNotFoundModal] = useState(false);

  const rules = {
    fullName: {
      presence: {allowEmpty: false},
    },
    dateOfBirth: {presence: true},
    sex: {presence: true},
    photoIDType: {presence: {allowEmpty: false, message: `^Photo ID Type can't be blank`}},
    hasDrugAllergies: {presence: true},
    idNumber: {
      presence: {message: `^ID Number can't be blank`},
      length: function () {
        if (inputs.photoIDType === PHOTO_ID_TYPE_VALUES.NRIC)
          return {
            maximum: 9,
            message: '^ID Number is too long (maximum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          };
        return false;
      },
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput<
    UserProfile & {isPickingDate: boolean; hasDrugAllergies?: boolean}
  >(
    {
      wpUserId: currentUser?.wpUserId || wpAuthUser.wpUserId || '',
      fullName: currentUser?.fullName || wpAuthUser.fullName || '',
      dateOfBirth: checkYMDIsValid(currentUser?.dateOfBirth) || convertDMYToYMD(wpAuthUser?.dateOfBirth),
      sex: currentUser?.sex || wpAuthUser?.sex || '',
      drugAllergies: currentUser?.drugAllergies || wpAuthUser?.drugAllergies || '',
      photoIDType: currentUser?.photoIDType || wpAuthUser?.photoIDType || '',
      idNumber: currentUser?.idNumber || wpAuthUser?.idNumber || '',
      photoIDURL: currentUser?.photoIDURL || wpAuthUser?.photoIDURL || '',
      isPickingDate: false,
      hasDrugAllergies: currentUser?.drugAllergies || wpAuthUser?.drugAllergies || undefined,
    },
    {rules},
  );

  const onClickSubmit = async () => {
    if (validation.getErrors()) {
      return;
    }
    if (inputs.hasDrugAllergies && !inputs.drugAllergies?.trim()) {
      addMessage('Please fill in your drug allergies', 'error');
      return;
    }
    if (!product?.name) {
      setIsShowPurchaseNotFoundModal(true);
      return;
    }
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_REFILL_KEYS.PROFILE,
        index: questionnaire?.index || '',
        data: {user: {...inputs, fullName: trimSpaces(inputs.fullName), idNumber: trimSpaces(inputs.idNumber)}},
      }),
    );
    if (inputs.sex === SEX_VALUES.male || isUnderAge(inputs.dateOfBirth)) {
      dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REFILL_KEYS.INVALID}));
      return;
    }
    dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REFILL_KEYS.LAST_BC_PURCHASE}));
  };

  const onUploadSuccess = (files: UploadResponse[]) => {
    setInputs({photoIDURL: files?.[0]?.accessUrl});
  };

  return (
    <>
      <PurchaseNotFoundModal
        isShowing={isShowPurchaseNotFoundModal}
        onDismissModal={() => setIsShowPurchaseNotFoundModal(false)}
      />
      <ViewImageModal
        isShowing={isShowPhotoId}
        onDismissModal={() => setIsShowPhotoId(false)}
        imgUrl={inputs.photoIDURL || ''}
        isCentered
      />
      <UploadImageModal
        isShowing={isShowUploadModal}
        onDismissModal={() => setIsShowUploadModal(false)}
        onSuccessUpload={onUploadSuccess}
      />
      <UiQuestionnaireLayout
        backIcon
        handleOnNext={onClickSubmit}
        disableNext={!inputs.photoIDURL}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiCard variant="rounded" width="100%" mt={spacing.small} mb={spacing.medium}>
            <UiFormControl id="fullName" isRequired isInvalid={!!validation?.errors?.fullName}>
              <UiFormLabel fontWeight="400" fontSize="xs">
                Full Name (as it appears in your NRIC/Passport)
              </UiFormLabel>
              <UiInput
                variant="flushed"
                value={inputs.fullName}
                onChange={({target}) => setInputs({fullName: target.value})}
                fontWeight="600"
              />
              {validation?.errors?.fullName && (
                <UiFormErrorMessage>{validation?.errors?.fullName[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>

            <UiFormControl id="dateOfBirth" isRequired isInvalid={!!validation?.errors?.dateOfBirth} mt={spacing.tiny}>
              <UiFormLabel fontWeight="400" fontSize="xs">
                Date of Birth
              </UiFormLabel>
              <AppDatePicker
                selected={!!inputs.dateOfBirth ? moment(inputs?.dateOfBirth) : undefined}
                onChange={value => {
                  setInputs({dateOfBirth: formatDate(value)});
                }}
                onCalendarOpen={() => {
                  setInputs({isPickingDate: true});
                }}
                onCalendarClose={(value: any) => {
                  setInputs({isPickingDate: false});
                }}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                customInput={<UiInput variant="flushed" fontWeight="600" />}
                dateFormat={inputs.isPickingDate ? CALENDAR_DATE_TIME_FORMAT : DATE_OF_BIRTH_TIME_FORMAT}
                maxDate={new Date()}
              />
              {validation?.errors?.dateOfBirth && (
                <UiFormErrorMessage>{validation?.errors?.dateOfBirth[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>

            <UiFormControl id="sex" isRequired isInvalid={!!validation?.errors?.sex} mt={spacing.tiny}>
              <UiFormLabel fontWeight="400" fontSize="xs">
                Sex
              </UiFormLabel>
              <CustomDropdown
                onChange={value => setInputs({sex: value?.value})}
                options={SEX_OPTIONS}
                defaultItem={SEX_OPTIONS.find(i => i.value === inputs.sex)}
                selectedFontWeight="600"
              />
              {validation?.errors?.sex && <UiFormErrorMessage>{validation?.errors?.sex[0]}</UiFormErrorMessage>}
            </UiFormControl>

            <DrugAllergies inputs={inputs} onChange={setInputs} errorMsg={validation?.errors?.hasDrugAllergies?.[0]} />

            <UiFormControl id="photoIDType" isRequired isInvalid={!!validation?.errors?.photoIDType} mt={spacing.tiny}>
              <UiFormLabel fontWeight="400" fontSize="xs">
                Photo ID Type
              </UiFormLabel>
              <CustomDropdown
                onChange={value => setInputs({photoIDType: value?.value})}
                options={PHOTO_ID_TYPE_OPTIONS}
                defaultItem={PHOTO_ID_TYPE_OPTIONS.find(i => i.value === inputs.photoIDType)}
                selectedFontWeight="600"
              />
              {validation?.errors?.photoIDType && (
                <UiFormErrorMessage>{validation?.errors?.photoIDType[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>
            <UiFormControl id="idNumber" isRequired isInvalid={!!validation?.errors?.idNumber} mt={spacing.tiny}>
              <UiFormLabel fontWeight="400" fontSize="xs">
                ID Number
              </UiFormLabel>
              <UiInput
                variant="flushed"
                value={inputs.idNumber}
                onChange={({target}) => setInputs({idNumber: target.value})}
                maxLength={inputs.photoIDType === PHOTO_ID_TYPE_VALUES.NRIC ? 9 : undefined}
                fontWeight="600"
              />
              {validation?.errors?.idNumber && (
                <UiFormErrorMessage>{validation?.errors?.idNumber[0]}</UiFormErrorMessage>
              )}
            </UiFormControl>
          </UiCard>
          <UiSimpleGrid columns={2} w="100%">
            <UiButton
              title="View Photo ID"
              variant="outline"
              mr={spacing.tiny}
              onClick={() => setIsShowPhotoId(true)}
              disabled={!inputs.photoIDURL}
            />
            <UiButton
              ml="10px"
              title={inputs.photoIDURL ? 'Upload New ID' : 'Upload ID'}
              variant="outline"
              onClick={() => setIsShowUploadModal(true)}
            />
          </UiSimpleGrid>
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire2Screen;
